
import foFooter from '@/components/footer/footer.vue'
import foHeader from '@/components/header/header.vue'
import foMark from '@/components/mark/mark.vue'
import {
  ALLOWED_LOCKER_SURVEY_BOOKING_STATUSES,
  ClientPageType,
  QuestionType,
  ROUTES,
} from '@/constants'
import { bookingMixin } from '@/mixins'
import ClientPageMapper from '@/services/clientPage/ClientPageMapper'
import BookingService from '@/services/booking/BookingService'
import { SnackBarType } from '@/store/types'
import { ClientPage, ClientPageComment, ClientPageScore } from '@/types'
import surveyAlreadySent from '@/views/CompletedSurvey/SurveyAlreadySent.vue'
import { HTTP_403_FORBIDDEN } from '@meltingpoint/shared-objects'
import mustache from 'mustache'
import mixins from 'vue-typed-mixins'

export default mixins(bookingMixin).extend({
  name: 'locker-survey',
  components: {
    foFooter,
    foHeader,
    foMark,
    surveyAlreadySent,
  },
  data: () => ({
    QuestionType,
    questions: [] as Array<ClientPageScore | ClientPageComment>,
    hasLoaded: false,
    language: '',
    loading: false,
  }),
  computed: {
    parsedContent(): string | undefined {
      if (this.clientPage?.config.customContent?.body) {
        return mustache.render(
          this.clientPage!.config.customContent?.body.replace(/<p>(.*?)<\/p>/gm, '$1')
            .replace(/&lt;/gm, '<')
            .replace(/&gt;/gm, '>')
            .replace(/&amp;/gm, '&')
            .replace(/&nbsp;/gm, ' ')
            .replace(/<br>/gm, ''),
          {},
        )
      }
    },
    canSubmit(): boolean {
      return this.questions
        .filter(({ required }: ClientPageScore | ClientPageComment) => required)
        .every((question: ClientPageScore | ClientPageComment) => {
          switch (question.type) {
            case QuestionType.SCORE_TYPE_SATISFACTION:
            case QuestionType.SCORE_TYPE_RECOMMENDATION:
              return (question as ClientPageScore).note > -1
            case QuestionType.CUSTOM:
              return Boolean((question as ClientPageComment).answer)
            default:
              return false
          }
        })
    },
  },
  async beforeMount() {
    const {
      query: { language },
    } = this.$route

    this.language = language?.toString() || ''
    await this.getBooking(this.$route.name!, this.language)

    if (
      !this.clientPage ||
      !this.booking ||
      !ALLOWED_LOCKER_SURVEY_BOOKING_STATUSES.includes(this.booking.status)
    ) {
      return this.$router.push({ name: ROUTES.NOT_FOUND })
    }
    this.hasLoaded = true
  },
  methods: {
    async submit(): Promise<void> {
      this.loading = true
      try {
        const { id } = this.$route.params
        const payload = ClientPageMapper.questionsToPayload(this.questions)

        payload.pageId = this.clientPage!.id

        await BookingService.createScore(id, payload)

        this.$store.dispatch('setSnackbar', {
          type: SnackBarType.SUCCESS,
          message: this.$t('messages.sent'),
        })
        this.$store.dispatch('setHeader', {
          content: this.headerData,
          config: this.clientPage?.config.header,
        })
        this.$router.push({
          name: ClientPageType.LOCKER.LOCKER_CONFIRMATION,
          params: { id, language: this.language || this.clientPage!.language },
        })
      } catch (err) {
        let message = this.$t('messages.error')
        if (err.response.status === HTTP_403_FORBIDDEN) {
          message = this.$t('messages.alreadySent')
        }

        this.$store.dispatch('setSnackbar', {
          type: SnackBarType.ERROR,
          message,
        })
      } finally {
        this.loading = false
      }
    },
    isScoreQuestion(type: string): boolean {
      return [
        QuestionType.SCORE_TYPE_RECOMMENDATION,
        QuestionType.SCORE_TYPE_SATISFACTION,
      ].includes(type as QuestionType)
    },
    loadQuestionsFromContent(): void {
      const {
        config: { content },
      } = this.clientPage as ClientPage
      this.questions = content ? ClientPageMapper.contentQuestionsToQuestions(content) : []
    },
  },
  watch: {
    clientPage(next, current) {
      if (current === undefined) {
        return
      }
      this.loadQuestionsFromContent()
    },
  },
})
